import { LineChart } from "@mantine/charts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eyeSeriesColor } from "@constants/resultColors";
import { PupillometryData } from "types/testTypes";

interface PupillometryChartProps {
  data: PupillometryData[];
  referenceLines?: any;
  height?: number | string;
  strokeWidth?: number;
}

export default function PupillometryChart(props: PupillometryChartProps) {
  const { data, referenceLines, height, strokeWidth } = props;
  const { t } = useTranslation();

  const yDomain = useMemo(() => {
    if (!data || data.length === 0) {
      return [2, 6];
    }

    const allPupilValues = [];
    data.forEach((d: PupillometryData) => {
      if (d.leftPupil !== null) {
        allPupilValues.push(d.leftPupil);
      }
      if (d.rightPupil !== null) {
        allPupilValues.push(d.rightPupil);
      }
    });

    if (allPupilValues.length === 0) {
      return [2, 6];
    }

    let min = Math.min(...allPupilValues);
    let max = Math.max(...allPupilValues);

    const padding = (max - min) * 0.1;
    min = Math.max(0, min - padding);
    max = max + padding;

    if (max - min < 1) {
      min = Math.floor(min);
      max = min + 1;
    }

    return [min, max];
  }, [data]);

  const ticks = useMemo(() => {
    const [min, max] = yDomain;
    const step = (max - min) / 9;
    return Array.from({ length: 10 }, (_, i) => min + i * step);
  }, [yDomain]);

  return (
    <LineChart
      h={height || 350}
      data={data}
      withDots={false}
      dataKey="timestamp"
      curveType="linear"
      withTooltip={false}
      strokeWidth={strokeWidth || 2}
      connectNulls
      yAxisProps={{
        ticks: ticks,
        domain: yDomain,
        allowDataOverflow: true,
        tickFormatter: (value) => `${value.toFixed(2)}`,
        label: {
          value: t("pupilDiameterMm"),
          position: "insideLeft",
          angle: -90,
          style: { textAnchor: "middle" },
          fontSize: 14,
        },
      }}
      xAxisProps={{
        interval: Math.max(1, Math.floor(data.length / 20)),
        tickFormatter: (value) => `${value.toFixed(2)}`,
        label: {
          value: t("timeSeconds"),
          position: "insideBottom",
          offset: -20,
        },
      }}
      series={[
        {
          name: "leftPupil" as keyof PupillometryData,
          color: eyeSeriesColor("left"),
          label: t("leftPupil"),
        },
        {
          name: "rightPupil" as keyof PupillometryData,
          color: eyeSeriesColor("right"),
          label: t("rightPupil"),
        },
      ]}
      withLegend
      legendProps={{
        verticalAlign: "bottom",
        height: 30,
        formatter: (value) => t(value),
      }}
      referenceLines={referenceLines ? referenceLines : null}
      tickLine="xy"
      gridAxis="xy"
    />
  );
}
